// MA-557 — the derived, server-side teacher-account fact (MA-DEC-0824 D1/D2/D4).
//
// userRole.ts's `pw_user_role` is a per-DEVICE localStorage string — real for
// routing (a parent on a borrowed device is real), but too fragile to gate
// compliance forks or data-creation on, because flipping it flips nothing
// about the ACCOUNT. MA-556's review found the live consequence: a
// class-owning teacher who flips their device to "parent" walks straight
// through teacherAddKidGate's role-first check (src/lib/teacherAddKidGate.ts
// pre-this-slice) and mints a duplicate family-side kid — the P3 identity
// trap.
//
// D1 — an account IS a teacher account iff it owns >=1 `classes` row (0023,
// `classes.teacher_id -> parents(id)`, RLS `classes_select` scopes reads to
// `teacher_id = auth.uid()`) OR holds a COMPLETED, un-revoked
// `school_authority_direct_v1` consent row (src/App/consentGate.js's
// `isCompletedSchoolDirectConsent` — reused, not reimplemented, so the two
// lane-law modules can never drift on what "completed" means). Both facts
// already live server-side under RLS the account itself can read; deriving
// beats a new `teacher_attested_at` column because it needs no migration, no
// founder prod-apply, and cannot go stale relative to the rows it summarizes.
//
// D4 — fail toward the STRICTER lane, always: any error or absent/malformed
// input derives `false` ("not a teacher account"). A compliance fork reading
// `false` falls to the parent COPPA lane; a creation gate reading `false`
// allows the family-style write. This mirrors consentGate.js's own
// fail-closed posture (consentGate.js:189-193) — a flaky read must never
// silently grant the teacher account's OWN identity to a stranger's session,
// and must never silently ADMIT a parent-labeled write for someone who never
// proved parental status either.
//
// This module is intentionally dependency-light (no React) except for the
// one hook at the bottom, so `deriveIsTeacherAccount` / `resolveEffectiveRole`
// stay plain-Node testable, mirroring consentGate.js's own "pure core +
// thin React seam" shape.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/supabaseClient.js";
import { fetchConsentRecords } from "@/supabaseClient.js";
import { captureSupabaseError } from "@/lib/sentry";
import { isCompletedSchoolDirectConsent } from "@/App/consentGate.js";
import { useAppState } from "@/App/AppStateContext.tsx";
import { isDevBypass } from "@/lib/persistence";
import type { UserRole } from "@/lib/userRole";

export type SupabaseClient = typeof supabase;

/**
 * PURE core. `true` iff `ownsClass === true` (strict — not merely truthy) OR
 * `consentRows` contains a completed `school_authority_direct_v1` row.
 * D4: any non-boolean `ownsClass` (undefined, null, a stale/garbage value)
 * and any non-array `consentRows` (undefined, null, an in-flight query's
 * `undefined` data) both resolve their half to `false` — the function never
 * throws and never treats an unproven input as proof.
 */
export function deriveIsTeacherAccount({
  ownsClass,
  consentRows,
}: {
  ownsClass?: unknown;
  consentRows?: unknown;
}): boolean {
  const ownsClassFact = ownsClass === true;
  const consentFact =
    Array.isArray(consentRows) && consentRows.some(isCompletedSchoolDirectConsent);
  return ownsClassFact || consentFact;
}

/**
 * Head-count read: does this account own at least one `classes` row?
 * Mirrors classApi.ts's `fetchTeacherClasses` filter shape (`teacher_id`
 * equality) but asks Postgres for a count only (`head: true`) rather than
 * the full row set — this call exists purely to answer a boolean, unlike
 * `fetchTeacherClasses` which the class-management UI needs full rows from.
 * THROWS on any read error (unlike teacherAddKidGate.ts's own
 * `fetchTeacherClassCountForGate`, which deliberately fails OPEN to 0 for
 * that gate's distinct MA-544 bootstrapping rationale) — this fact-level
 * fetch is the one D4 asks to fail CLOSED, so every caller must catch and
 * derive `false`, never silently treat a failed read as "owns zero classes"
 * and therefore satisfy nothing quietly.
 */
export async function fetchOwnsClass(
  teacherId: string,
  client: SupabaseClient = supabase,
): Promise<boolean> {
  const { count, error } = await client
    .from("classes")
    .select("id", { count: "exact", head: true })
    .eq("teacher_id", teacherId);
  if (error) {
    captureSupabaseError("fetchOwnsClass failed", error, { teacherId });
    throw error;
  }
  return (count ?? 0) > 0;
}

/**
 * The device/account resolution D2 describes: `deviceRole` (from
 * `getUserRole()`) is the routing/home-screen PREFERENCE; `isTeacherAccount`
 * is the D1 server-side fact. The effective role a compliance fork or
 * creation gate should key on is "teacher" only when BOTH agree — a device
 * flipped to "teacher" on a non-teacher account still resolves to "parent"
 * (falls to the stricter COPPA lane), and a device flipped to "parent" on a
 * genuine teacher account does NOT downgrade the account's own compliance
 * lane requirement (that half is teacherAddKidGate.ts's job, which keys on
 * the fact directly rather than through this resolver — see its header).
 * Any non-`true` `isTeacherAccount` (including a stale/loading/error `false`
 * or `undefined`) resolves to "parent" — D4 again.
 */
export function resolveEffectiveRole(
  deviceRole: UserRole,
  isTeacherAccount: unknown,
): UserRole {
  return deviceRole === "teacher" && isTeacherAccount === true ? "teacher" : "parent";
}

/**
 * React seam. Reuses the SAME `["consent-records", userId]` query the app
 * already runs (ConsentNeededNotice / SettingsPrivacy in
 * ParentSettingsSections.tsx both key on this exact tuple) so this hook
 * shares react-query's cache instead of opening a second network round trip
 * for the same rows, and adds one sibling query for the class-ownership
 * head-count. `enabled` mirrors those call sites' own gate (`!!userId &&
 * !isDevBypass()`) so e2e/dev (no real session) never issues either read.
 *
 * On ANY error (either query, or simply not yet resolved) this returns
 * `isTeacherAccount: false` — it NEVER throws to render (D4: an in-flight or
 * failed read is treated as "not proven", the stricter/lower-privilege
 * reading for both consumers: a compliance fork falls to COPPA, a
 * creation-gate check the fact still satisfies falls to blocked).
 *
 * STALENESS NOTE (MA-557 bootstrap requirement): this hook's `staleTime` is
 * 60s, so a component that reads it via this hook right after writing a
 * fresh consent row could still see the pre-write cached value until the
 * query invalidates/refetches. useCloudSync.js's `createCloudStudentFn`
 * therefore does NOT consume this hook — it derives the fact from a FRESH,
 * uncached fetch taken at the exact moment of the gated write (see the
 * comment there). This hook is for render-time UI (compliance banners,
 * status labels) where a brief staleness window is the same acceptable
 * trade-off `ConsentNeededNotice`/`SettingsPrivacy` already accept for their
 * own `consent-records` query.
 */
export function useIsTeacherAccount(): { isTeacherAccount: boolean; loading: boolean } {
  const app = useAppState();
  const userId: string | null = app?.user?.id ?? null;
  const enabled = !!userId && !isDevBypass();

  const consentQuery = useQuery({
    queryKey: ["consent-records", userId],
    queryFn: () => fetchConsentRecords(userId as string),
    enabled,
    staleTime: 60_000,
  });
  const ownsClassQuery = useQuery({
    queryKey: ["owns-class", userId],
    queryFn: () => fetchOwnsClass(userId as string),
    enabled,
    staleTime: 60_000,
  });

  const isTeacherAccount =
    enabled && consentQuery.isSuccess && ownsClassQuery.isSuccess
      ? deriveIsTeacherAccount({
          ownsClass: ownsClassQuery.data,
          consentRows: consentQuery.data,
        })
      : false;

  const loading = enabled && (consentQuery.isLoading || ownsClassQuery.isLoading);

  return { isTeacherAccount, loading };
}
